import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MarketplaceRow {
  readonly templateId: string;
  readonly templateCode: string;
  readonly templateName: string;
  readonly templateType: string;
  readonly templateCategory: string | null;
  readonly installCount: number;
}

const TEMPLATE_TYPES = ['', 'FORM', 'WORKFLOW', 'REPORT', 'DASHBOARD', 'APPLICATION', 'WIDGET'];
const CATEGORIES = ['', 'VENDOR_REGISTRATION', 'SURVEY', 'AUDIT_CHECKLIST', 'APPROVAL_REQUEST', 'INSPECTION_FORM', 'COMPLIANCE_FORM', 'EXPENSE_CLAIM', 'ASSET_VERIFICATION', 'GENERAL'];

/**
 * M28 — Internal Marketplace (§21 / §32). The published, install-ready subset of
 * the template library: only template_library (T374) rows flagged
 * isMarketplace = true are shown here (marketplaceOnly filter). Lists each
 * shared asset with its type, category and install count so a citizen developer
 * can discover and one-click install curated building blocks into a new
 * application. Install is wired to the guarded /v1/lcnc/templates/{id}/install
 * endpoint. Filterable by template type and category.
 */
export function M28MarketplacePage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<MarketplaceRow[]>([]);
  const [templateType, setTemplateType] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('marketplaceOnly', 'true');
    if (templateType) params.set('templateType', templateType);
    if (templateCategory) params.set('templateCategory', templateCategory);
    apiRequest<{ data: MarketplaceRow[] }>(`/v1/lcnc/templates?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, templateType, templateCategory, reload]);

  const install = async (t: MarketplaceRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/lcnc/templates/${t.templateId}/install`, { method: 'POST', body: {}, token });
      setNotice(tr('m28.marketplace.noticeInstalled', { code: t.templateCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.status === 403
          ? tr('m28.marketplace.notAuthorised')
          : (err as Error).message,
      );
    }
  };

  return (
    <main aria-label="marketplace">
      <h1>{tr('m28.marketplace.title')}</h1>
      <div className="filters">
        <label>
          {tr('m28.marketplace.filterType')}
          <select aria-label="filter-template-type" value={templateType} onChange={(e) => setTemplateType(e.target.value)}>
            {TEMPLATE_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.marketplace.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.marketplace.filterCategory')}
          <select aria-label="filter-template-category" value={templateCategory} onChange={(e) => setTemplateCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || tr('m28.marketplace.all')}</option>)}
          </select>
        </label>
      </div>
      {actionError && <p role="alert" aria-label="marketplace-error">{actionError}</p>}
      {notice && <p aria-label="marketplace-notice">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.marketplace.loading')}</p> : (
        <>
          <p aria-label="marketplace-count">{tr('m28.marketplace.count', { count: rows.length })}</p>
          <table aria-label="marketplace-table">
            <thead><tr><th>{tr('m28.marketplace.colCode')}</th><th>{tr('m28.marketplace.colName')}</th><th>{tr('m28.marketplace.colType')}</th><th>{tr('m28.marketplace.colCategory')}</th><th>{tr('m28.marketplace.colInstalls')}</th><th>{tr('m28.marketplace.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.templateId}>
                  <td>{t.templateCode}</td><td>{t.templateName}</td><td>{t.templateType}</td>
                  <td>{t.templateCategory ?? '—'}</td><td>{t.installCount}</td>
                  <td><button type="button" aria-label={`install-${t.templateCode}`} onClick={() => void install(t)}>{tr('m28.marketplace.btnInstall')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
